import WhatsappCallsAPI from 'dashboard/api/whatsappCalls';
import { emitter } from 'shared/helpers/mitt';

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

class WhatsappVoiceClient extends EventTarget {
  constructor() {
    super();
    this.peerConnection = null;
    this.localStream = null;
    this.remoteStream = null;
    this.callId = null;
    this.conversationId = null;

    emitter.on('whatsapp.call.outbound_connected', data =>
      this.handleOutboundConnected(data)
    );
    emitter.on('whatsapp.call.ended', data => this.handleCallEnded(data));
  }

  async initiateCall(conversationId) {
    this.conversationId = conversationId;

    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: false,
    });

    this.peerConnection = new RTCPeerConnection({
      iceServers: ICE_SERVERS,
    });

    this.localStream.getTracks().forEach(track => {
      this.peerConnection.addTrack(track, this.localStream);
    });

    this.remoteStream = new MediaStream();
    this.peerConnection.ontrack = event => {
      event.streams[0].getTracks().forEach(track => {
        this.remoteStream.addTrack(track);
      });
      this.playRemoteAudio();
    };

    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);

    await this.waitForIceCandidates();

    const result = await WhatsappCallsAPI.initiate({
      conversationId,
      sdpOffer: this.peerConnection.localDescription.sdp,
    });

    this.callId = result.id;
    return result;
  }

  async handleOutboundConnected(data) {
    if (!this.peerConnection || !data.sdp_answer) return;
    if (this.callId && data.id !== this.callId) return;

    const sdpAnswer = data.sdp_answer;
    await this.peerConnection.setRemoteDescription(
      new RTCSessionDescription({ type: 'answer', sdp: sdpAnswer })
    );
  }

  async handleIncomingCall(data) {
    this.callId = data.id;
    this.conversationId = data.conversation_id;

    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: false,
    });

    this.peerConnection = new RTCPeerConnection({
      iceServers: data.ice_servers || ICE_SERVERS,
    });

    this.localStream.getTracks().forEach(track => {
      this.peerConnection.addTrack(track, this.localStream);
    });

    this.remoteStream = new MediaStream();
    this.peerConnection.ontrack = event => {
      event.streams[0].getTracks().forEach(track => {
        this.remoteStream.addTrack(track);
      });
      this.playRemoteAudio();
    };

    await this.peerConnection.setRemoteDescription(
      new RTCSessionDescription({ type: 'offer', sdp: data.sdp_offer })
    );

    const answer = await this.peerConnection.createAnswer();
    await this.peerConnection.setLocalDescription(answer);

    await this.waitForIceCandidates();

    await WhatsappCallsAPI.accept(
      this.callId,
      this.peerConnection.localDescription.sdp
    );
  }

  handleCallEnded(data) {
    if (this.callId && data.id !== this.callId) return;
    this.cleanup();
    this.dispatchEvent(new CustomEvent('call:disconnected'));
  }

  async endCall() {
    if (this.callId) {
      await WhatsappCallsAPI.terminate(this.callId).catch(() => {});
    }
    this.cleanup();
    this.dispatchEvent(new CustomEvent('call:disconnected'));
  }

  async rejectCall() {
    if (this.callId) {
      await WhatsappCallsAPI.reject(this.callId).catch(() => {});
    }
    this.cleanup();
  }

  playRemoteAudio() {
    if (!this.audioElement) {
      this.audioElement = new Audio();
      this.audioElement.autoplay = true;
    }
    this.audioElement.srcObject = this.remoteStream;
  }

  waitForIceCandidates() {
    return new Promise(resolve => {
      if (this.peerConnection.iceGatheringState === 'complete') {
        resolve();
        return;
      }

      const timeout = setTimeout(resolve, 3000);
      this.peerConnection.onicegatheringstatechange = () => {
        if (this.peerConnection.iceGatheringState === 'complete') {
          clearTimeout(timeout);
          resolve();
        }
      };
    });
  }

  cleanup() {
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }
    if (this.audioElement) {
      this.audioElement.srcObject = null;
      this.audioElement = null;
    }
    this.remoteStream = null;
    this.callId = null;
    this.conversationId = null;
  }

  get hasActiveConnection() {
    return (
      this.peerConnection &&
      this.peerConnection.connectionState !== 'closed' &&
      this.peerConnection.connectionState !== 'failed'
    );
  }
}

export default new WhatsappVoiceClient();
